// Type-only: erased at compile time, so this stays a pure helper with no component dependency and
// no React in its bundle. The four corners already have one definition and it lives there.
import type { WidgetPosition } from '../components/WidgetChatInterface';

// Where the launcher is allowed to rest.
//
// Destinations rather than coordinates, because a named destination cannot leave the screen: no
// clamping, no deriving a position back out of a rectangle, and nothing to reconcile when the window
// is resized or a monitor is undocked. That was most of the free-positioning model this replaces.
//
// Six for the bar and four for the widget, and the difference is not arbitrary — the bar is wide, so
// its horizontal choice is meaningful in thirds; the bubble is small, so only corners read as
// distinct places to put it.

export type LauncherSlot =
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'top-left'
  | 'top-center'
  | 'top-right';

export const LAUNCHER_SLOTS: ReadonlyArray<LauncherSlot> = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right'
];

export const WIDGET_CORNERS: ReadonlyArray<WidgetPosition> = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right'
];

// The bar has always sat centred on the bottom edge. Keeping that as the default is what makes this
// invisible to a visitor who never drags.
export const DEFAULT_LAUNCHER_SLOT: LauncherSlot = 'bottom-center';

export type Point = { x: number; y: number };
export type Viewport = { width: number; height: number };

// Every side is written on purpose, including the ones set to 'auto'. The host assigns whatever it
// receives, so a side left out keeps its previous value and the frame stretches between two edges.
const LAUNCHER_SLOT_CSS: Record<LauncherSlot, Record<string, string>> = {
  'bottom-center': {
    top: 'auto',
    bottom: '0',
    left: '50%',
    right: 'auto',
    transform: 'translateX(-50%)'
  },
  'bottom-left': { top: 'auto', bottom: '0', left: '0', right: 'auto', transform: 'none' },
  'bottom-right': { top: 'auto', bottom: '0', left: 'auto', right: '0', transform: 'none' },
  'top-center': {
    top: '0',
    bottom: 'auto',
    left: '50%',
    right: 'auto',
    transform: 'translateX(-50%)'
  },
  'top-left': { top: '0', bottom: 'auto', left: '0', right: 'auto', transform: 'none' },
  'top-right': { top: '0', bottom: 'auto', left: 'auto', right: '0', transform: 'none' }
};

const WIDGET_CORNER_CSS: Record<WidgetPosition, Record<string, string>> = {
  'bottom-left': LAUNCHER_SLOT_CSS['bottom-left'],
  'bottom-right': LAUNCHER_SLOT_CSS['bottom-right'],
  'top-left': LAUNCHER_SLOT_CSS['top-left'],
  'top-right': LAUNCHER_SLOT_CSS['top-right']
};

export function launcherSlotCss(slot: LauncherSlot): Record<string, string> {
  return LAUNCHER_SLOT_CSS[slot];
}

export function widgetCornerCss(corner: WidgetPosition): Record<string, string> {
  return WIDGET_CORNER_CSS[corner];
}

// Which destination a point belongs to. Split by thirds horizontally and halves vertically rather
// than by distance to each destination's anchor: distance would make the centre a much larger target
// than the sides, and the visitor is choosing between regions, not aiming at points.
//
// Boundaries resolve downward — exactly on a third goes to the region on its right, exactly on the
// middle goes to the bottom — so the answer is always defined and never flickers between two.
export function nearestLauncherSlot(point: Point, viewport: Viewport): LauncherSlot {
  const vertical = point.y < viewport.height / 2 ? 'top' : 'bottom';
  const third = viewport.width / 3;
  const horizontal = point.x < third ? 'left' : point.x < third * 2 ? 'center' : 'right';
  return `${vertical}-${horizontal}` as LauncherSlot;
}

export function nearestWidgetCorner(point: Point, viewport: Viewport): WidgetPosition {
  const vertical = point.y < viewport.height / 2 ? 'top' : 'bottom';
  const horizontal = point.x < viewport.width / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}` as WidgetPosition;
}

// Keyboard movement between destinations. With a discrete set there is nothing to nudge — an arrow
// means "the next one over", which is both simpler to reason about and faster to use than pushing a
// box a few pixels at a time.
//
// It stops at the ends rather than wrapping: a visitor holding the left arrow expects to arrive at
// the left edge and stay there, not to reappear on the right.
const COLUMNS = ['left', 'center', 'right'] as const;

export function stepLauncherSlot(slot: LauncherSlot, dx: number, dy: number): LauncherSlot {
  const [row, column] = slot.split('-') as ['top' | 'bottom', (typeof COLUMNS)[number]];
  const nextRow = dy === 0 ? row : dy < 0 ? 'top' : 'bottom';
  const index = COLUMNS.indexOf(column);
  const nextColumn = COLUMNS[Math.min(Math.max(index + Math.sign(dx), 0), COLUMNS.length - 1)];
  return `${nextRow}-${nextColumn}` as LauncherSlot;
}

// The widget has no middle column, so an arrow simply flips the side it is on. Stepping it through
// the bar's helper would land it on a destination that does not exist for a bubble.
export function stepWidgetCorner(corner: WidgetPosition, dx: number, dy: number): WidgetPosition {
  const [row, column] = corner.split('-') as ['top' | 'bottom', 'left' | 'right'];
  const nextRow = dy === 0 ? row : dy < 0 ? 'top' : 'bottom';
  const nextColumn = dx === 0 ? column : dx < 0 ? 'left' : 'right';
  return `${nextRow}-${nextColumn}` as WidgetPosition;
}

const SLOT_KEY_PREFIX = 'gd-launcher-slot-';
const CORNER_KEY_PREFIX = 'gd-widget-corner-';

const isLauncherSlot = (value: unknown): value is LauncherSlot =>
  typeof value === 'string' && (LAUNCHER_SLOTS as ReadonlyArray<string>).includes(value);

const isWidgetCorner = (value: unknown): value is WidgetPosition =>
  typeof value === 'string' && (WIDGET_CORNERS as ReadonlyArray<string>).includes(value);

// Fails open in every direction: blocked storage, a value from an older shape, or anything a hand
// edit left behind all read as "never chosen". A launcher that throws here would take the whole
// widget down over a preference.
export function loadLauncherSlot(botKey: string): LauncherSlot | null {
  try {
    const raw = window.localStorage.getItem(`${SLOT_KEY_PREFIX}${botKey}`);
    return isLauncherSlot(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function saveLauncherSlot(botKey: string, slot: LauncherSlot): void {
  try {
    window.localStorage.setItem(`${SLOT_KEY_PREFIX}${botKey}`, slot);
  } catch {
    /* fail-open: the visitor loses the preference, not the widget */
  }
}

export function loadWidgetCorner(botKey: string): WidgetPosition | null {
  try {
    const raw = window.localStorage.getItem(`${CORNER_KEY_PREFIX}${botKey}`);
    return isWidgetCorner(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function saveWidgetCorner(botKey: string, corner: WidgetPosition): void {
  try {
    window.localStorage.setItem(`${CORNER_KEY_PREFIX}${botKey}`, corner);
  } catch {
    /* fail-open */
  }
}
